/** SUNTRONIX ONE — Internal Sales CRM Dashboard.
 *  Suntronix employee-only workspace. Customers cannot reach these routes.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText, ShoppingCart, Receipt, ArrowRight,
  RefreshCw, TrendingUp, AlertCircle, Landmark, Truck, PackageCheck,
  ReceiptText, ClipboardList, Activity,
} from "lucide-react";
import { AdminChip } from "@/components/app/AdminMode";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

export default function CRMDashboard() {
  const [stats, setStats] = useState(null);
  const [alertsRollup, setAlertsRollup] = useState(null);
  const [busy, setBusy]   = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [dash, alerts] = await Promise.all([
        api.get("/vtiger/dashboard"),
        api.get("/vtiger/po-amendment-alerts/rollup").catch(() => ({ data: null })),
      ]);
      setStats(dash.data);
      setAlertsRollup(alerts.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load CRM dashboard");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);




  return (
    <div className="space-y-6 max-w-[1500px]" data-testid="crm-dashboard">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-emerald-700">
            Internal · Sales CRM · Suntronix employees only
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <Landmark className="h-7 w-7 text-emerald-700" strokeWidth={1.6} />
            Commercial Workspace
            <AdminChip area="dashboard" label="Edit dashboard" testid="admin-chip-crm-dashboard" />
            <AdminChip area="modules" label="Edit navigation" testid="admin-chip-crm-modules" />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Quotations, Customer POs and Vendor POs managed natively in ONE.
          </p>
        </div>
        <div className="flex gap-2">
          
          
          
          <Button variant="outline" onClick={load} disabled={busy}
            data-testid="crm-refresh">
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
      </header>

      {/* PO Amendment Alerts banner */}
      {alertsRollup && alertsRollup.open_total > 0 && (
        <Link to="/commercial/crm/customer-pos"
          className="block border border-rose-200 bg-rose-50 rounded-md p-3 hover:bg-rose-100"
          data-testid="po-alerts-banner">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-rose-700" />
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-rose-900">
                {alertsRollup.open_total} open PO amendment alert{alertsRollup.open_total === 1 ? "" : "s"}
                {alertsRollup.open_high > 0 && (
                  <span className="ml-2 text-[11px] font-mono">
                    · {alertsRollup.open_high} HIGH
                  </span>
                )}
              </div>
              <div className="text-[11px] text-rose-700 mt-0.5">
                Customer POs where totals differ from the quoted amount. Review before shipping.
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-rose-700" />
          </div>
        </Link>
      )}

      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Kpi label="Pipeline" value={fmtINR(stats?.pipeline_inr)}
             tone="warn" testId="kpi-pipeline" />
        <Kpi label="Won (INR)" value={fmtINR(stats?.won_inr)}
             tone="ok" testId="kpi-won" />
        <Kpi label="Draft quotes" value={stats?.draft_quotations ?? 0}
             testId="kpi-draft" />
        <Kpi label="Sent quotes" value={stats?.sent_quotations ?? 0}
             testId="kpi-sent" />
        <Kpi label="Accepted" value={stats?.accepted_quotations ?? 0}
             tone="ok" testId="kpi-accepted" />
        <Kpi label="Customer POs" value={stats?.customer_pos ?? 0}
             testId="kpi-cpo" />
      </div>

      {/* Nav tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <NavTile to="/commercial/overview" icon={Activity}
          title="Pipeline Overview"
          subtitle="Live commercial pipeline · quote to billing · drill-down to records"
          testId="tile-pipeline-overview"
          accent />
        <NavTile to="/commercial/crm/pipeline" icon={TrendingUp}
          title="Sales Pipeline"
          subtitle="Live value-by-stage · auto from quotes"
          testId="tile-pipeline" accent />
        <NavTile to="/commercial/crm/opportunities" icon={FileText}
          title="Opportunities"
          subtitle="Deal pipeline · weighted forecast"
          testId="tile-opportunities" />
        <NavTile to="/commercial/crm/quotations" icon={FileText}
          title="Quotations"
          subtitle="Draft, review and send quotations"
          testId="tile-quotations" />
        <NavTile to="/commercial/customer-po" icon={ReceiptText}
          title="Customer PO — Intake"
          subtitle="Capture POs received from customers · match, link, cancel"
          testId="tile-customer-po-intake"
          accent />
        <NavTile to="/commercial/one-orders" icon={ClipboardList}
          title="SUNTRONIX ONE Orders"
          subtitle="Operational orders · STX-ORD-YYYY-NNNNNN"
          testId="tile-one-orders"
          accent />
        <NavTile to="/commercial/crm/customer-pos" icon={Receipt}
          title="Customer POs"
          subtitle="Purchase orders from customers"
          testId="tile-customer-pos" />
        <NavTile to="/commercial/crm/sales-orders" icon={Truck}
          title="Sales Orders"
          subtitle="Fulfilment lifecycle · from validated PO to invoice"
          testId="tile-sales-orders"
          accent />
        <NavTile to="/commercial/crm/order-ready" icon={PackageCheck}
          title="Order Ready — Ship &amp; Bill"
          subtitle="Shipped but not invoiced · month-end triage"
          testId="tile-order-ready" />
        {stats?.cost_visible ? (
          <NavTile to="/commercial/crm/vendor-pos" icon={ShoppingCart}
            title="Vendor POs"
            subtitle="Internal procurement · costs & margins"
            testId="tile-vendor-pos" restricted />
        ) : (
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-5 text-slate-500"
            data-testid="tile-vendor-pos-locked">
            <AlertCircle className="h-5 w-5 mb-2" />
            <div className="text-[14px] font-semibold">Vendor POs</div>
            <div className="text-[11px] mt-1">
              Restricted to admin / management / procurement / finance roles.
            </div>
          </div>
        )}
        <NavTile to="/commercial/entitlements" icon={FileText}
          title="Entitlements & SLA"
          subtitle="Contracts · entitlements · SLA policies · renewals"
          testId="tile-contracts" />
      </div>
    </div>
  );
}


function Kpi({ label, value, tone, testId }) {
  const tint = tone === "ok" ? "text-emerald-700"
             : tone === "warn" ? "text-amber-700"
             : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={"text-2xl font-heading font-semibold mt-1 " + tint}>{value ?? "—"}</div>
    </div>
  );
}

function NavTile({ to, icon: Icon, title, subtitle, testId, restricted, accent }) {
  return (
    <Link to={to}
      className={"group bg-white border rounded-lg p-5 hover:shadow-sm transition " +
        (accent
          ? "border-emerald-300 bg-gradient-to-br from-emerald-50 to-white hover:border-emerald-500"
          : "border-slate-200 hover:border-emerald-500")}
      data-testid={testId}>
      <div className="flex items-start justify-between">
        <Icon className="h-6 w-6 text-emerald-700" strokeWidth={1.6} />
        {restricted && (
          <Badge className="text-[9px] bg-amber-50 text-amber-800 border-amber-200">Cost visible</Badge>
        )}
        {accent && (
          <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border-emerald-200">New</Badge>
        )}
      </div>
      <div className="mt-3">
        <div className="text-[15px] font-semibold text-slate-900">{title}</div>
        <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      <div className="mt-3 text-[11px] text-emerald-700 group-hover:underline flex items-center gap-1">
        Open <ArrowRight className="h-3 w-3" />
      </div>
    </Link>
  );
}
